// The packing-detail section of the Assembly control — the eight axes that say
// how a kit is padded, closed, boxed, over-boxed and labelled (ADR-018
// Decision 5).
//
// There is no bespoke renderer here on purpose: the control IS SchemaForm, bound
// to the assembly snapshot instead of a line's config and narrowed to the
// quote-scope groups. A hand-rolled set of eight inputs would evaluate
// conditional_show its own way, and a second evaluator of one predicate is what
// ADR-011 exists to prevent.
//
// The snapshot's axis keys are FLAT (evalExpr reads `values[field]`), and the
// backend reports an assembly miss under an `assembly.`-prefixed key, so the
// prefix is stripped HERE — at the reporting boundary — rather than being
// authored into the schema.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ASSEMBLY_FIELD_PREFIX, type AssemblySnapshot, type FieldValues, type FormSchemaDto } from 'shared';

import { useFieldIdPrefix } from './fieldDomId.js';
import { SchemaForm } from './SchemaForm.js';
import { localizeSchemaLabels } from './schemaLabels.js';

type AssemblyDetailFieldsProps = {
  // The schema ALREADY narrowed to the quote-scope groups — the same narrowing
  // the caller's scrub runs against, so the two cannot disagree about which
  // fields exist. SchemaForm narrows again and gets the same object back, which
  // is what keeps one narrowing from becoming two.
  schema: FormSchemaDto;
  assembly: AssemblySnapshot;
  onChange: (next: AssemblySnapshot) => void;
  // Keyed as the rest of the Assembly control keys them: `assembly.<field>`.
  errors: Record<string, string>;
  disabled?: boolean;
  // The level this section's title renders at, matched to the assembly lists
  // beside it. See AssemblyPanel's own prop.
  headingLevel?: 'h4' | 'h5';
};

export function AssemblyDetailFields({
  schema,
  assembly,
  onChange,
  errors,
  disabled = false,
  headingLevel = 'h5',
}: AssemblyDetailFieldsProps): JSX.Element {
  const { t, i18n } = useTranslation(['quote', 'common']);
  // The axes are SchemaForm fields, so they take the namespacing through
  // SchemaForm's own prop rather than the context the hand-written controls read
  // — one convention per renderer, the same value behind both (fieldDomId).
  const idPrefix = useFieldIdPrefix();
  const localized = useMemo(
    () => localizeSchemaLabels(schema, t),
    // t is re-created per language change; depending on the language directly
    // keeps the memo from re-running on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, i18n.language],
  );

  // Two shapes arrive here. A miss the portal's own gates report is prefixed
  // (`assembly.kit_type`), and the prefix is stripped so it meets the flat field
  // key the renderer draws. A miss the ERP reports names the schema field
  // directly, and QuoteEdit leaves a quote-scope key bare for exactly this
  // reason — a per-line key never reaches here, because those are namespaced to
  // their project.
  const fieldErrors = useMemo(() => {
    const out: Record<string, string> = {};
    const prefix = `${ASSEMBLY_FIELD_PREFIX}.`;
    for (const [key, message] of Object.entries(errors)) {
      out[key.startsWith(prefix) ? key.slice(prefix.length) : key] = message;
    }
    return out;
  }, [errors]);

  return (
    // A submitted order still renders this section so the customer can read back
    // what they asked for — as STATIC TEXT, via `readOnly`, not as a form it
    // cannot use. Two things hold that together and each one alone breaks it.
    // The fieldset must NOT carry `disabled`: a natively disabled input is out of
    // the tab order, so eight radio/checkbox groups holding the customer's
    // answers in their checked state are unreachable by keyboard and screen
    // reader alike, and `disabled` on the inputs themselves has exactly the same
    // effect. And `readOnly` must be passed, or with the fieldset open a
    // submitted order is editable — SchemaForm's own `disabled` reaches its
    // upload slots only. `readOnly` renders no editable control for any field
    // type, which is what makes the fieldset's `disabled` unnecessary rather than
    // merely absent.
    <fieldset className="assembly-details">
      <SchemaForm
        scope="quote"
        schema={localized}
        values={assembly as FieldValues}
        onChange={(next) => onChange(next as AssemblySnapshot)}
        errors={fieldErrors}
        disabled={disabled}
        readOnly={disabled}
        headingLevel={headingLevel}
        {...(idPrefix ? { fieldIdPrefix: idPrefix } : {})}
      />
    </fieldset>
  );
}
